// Adding a dashboard or config from this computer: the file's chosen or dropped, said back as what it is and what it
// becomes (a new item, or the next version of one already there), then sent with its progress. Sending can pause and
// go on, or be cancelled; a part that fails is tried again, and on going on only the rest is sent. It goes to the
// owner's library, or, from the admin dashboard, to TUNE's items (`target`).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { data, type Api } from '../../api';
import { useAuth } from '../../auth';
import { kindWord, size } from '../../items';
import { Uploader, type UploadState } from '../../lib/uploader';
import { keys } from '../../queries';
import { Banner, Button, FileDrop, ProgressBar, Sheet, Spinner, useToast } from '../../ui';
import { describeFile, type Described } from '../../upload';
import { ItemThumb, uploadProblem } from './parts';
import s from './Library.module.css';

/** What the library takes: dashboards (.tunetheme) and configs (.tuneconfig). */
const ACCEPT = '.tunetheme,.tuneconfig,application/zip,text/xml,application/xml';

/** Where a file goes, and how that's said. */
export interface AddTarget {
  api: Api;
  /** What's there already, to say whether a file is new or the next version of one. */
  existing: ReadonlyArray<{ id: string; identity: string; name: string }> | undefined;
  /** The page an item has, once it's there. */
  opened: (itemId: string) => string;
  words: {
    next: (name: string) => string;
    fresh: string;
    finishing: string;
    added: string;
    existing: string;
  };
  /** Once it's there: what to read again. */
  changed: (itemId: string) => Promise<unknown>;
}

/** The owner's library. */
function useOwnerTarget(enabled: boolean): AddTarget {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const library = useQuery({ queryKey: keys.library, queryFn: () => data(api.GET('/library')), enabled });
  return useMemo(
    () => ({
      api,
      existing: library.data?.items,
      opened: (id: string) => `/library/${id}`,
      words: {
        next: (name: string) =>
          `It becomes the newest version of ${name}, and goes to your TUNE MFDs at their next sync.`,
        fresh: 'It becomes a new item in your library, and goes to your TUNE MFDs at their next sync.',
        finishing: 'Putting it in your library…',
        added: 'Added to your library.',
        existing: 'That version was in your library already.',
      },
      changed: (id: string) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: keys.library }),
          queryClient.invalidateQueries({ queryKey: keys.item(id) }),
        ]),
    }),
    [api, library.data, queryClient],
  );
}

export function AddFileSheet({
  open,
  onClose,
  item,
  target,
}: {
  open: boolean;
  onClose: () => void;
  /** Adding the next version of this item, rather than whatever the file is. */
  item?: { id: string; identity: string; name: string };
  /** Where it goes: the owner's library, unless said. */
  target?: AddTarget;
}) {
  // What's on its way, so closing the sheet cancels it.
  const sending = useRef<Uploader | null>(null);
  const close = () => {
    const up = sending.current;
    if (up && (up.state.kind === 'sending' || up.state.kind === 'paused' || up.state.kind === 'failed')) {
      void up.cancel();
    }
    sending.current = null;
    onClose();
  };
  return (
    <Sheet open={open} onClose={close} title={item ? 'Add a new version' : 'Add a file'}>
      <AddFile item={item} target={target} onSending={(up) => (sending.current = up)} />
    </Sheet>
  );
}

type Stage =
  | { kind: 'choose'; problem: string | null }
  | { kind: 'reading'; file: File }
  | { kind: 'ready'; file: File; described: Described; next: { id: string; name: string } | null };

function AddFile({
  item,
  target: given,
  onSending,
}: {
  item?: { id: string; identity: string; name: string };
  target?: AddTarget;
  onSending: (up: Uploader | null) => void;
}) {
  const owner = useOwnerTarget(!given);
  const target = given ?? owner;
  const navigate = useNavigate();
  const say = useToast();
  const [stage, setStage] = useState<Stage>({ kind: 'choose', problem: null });
  const [state, setState] = useState<UploadState | null>(null);
  const uploader = useRef<Uploader | null>(null);

  const choose = async (file: File | null) => {
    if (!file) {
      setStage({ kind: 'choose', problem: null });
      return;
    }
    setStage({ kind: 'reading', file });
    try {
      const described = await describeFile(file);
      if (item && described.identity.toLowerCase() !== item.identity.toLowerCase()) {
        setStage({
          kind: 'choose',
          problem: `That's ${described.name}, not ${item.name}: a new version is the same dashboard or config, changed.`,
        });
        return;
      }
      const same = item
        ? { id: item.id, name: item.name }
        : (target.existing?.find((i) => i.identity.toLowerCase() === described.identity.toLowerCase()) ?? null);
      setStage({ kind: 'ready', file, described, next: same ? { id: same.id, name: same.name } : null });
    } catch (error) {
      setStage({ kind: 'choose', problem: uploadProblem(error) });
    }
  };

  const send = () => {
    if (stage.kind !== 'ready') {
      return;
    }
    const up = new Uploader(
      target.api,
      stage.file,
      stage.next ? { itemId: stage.next.id } : { newItem: stage.described },
      (next) => {
        setState(next);
        if (next.kind === 'done') {
          finished(next);
        }
      },
    );
    uploader.current = up;
    onSending(up);
    void up.start();
  };

  // Sent: what's there reads again, and the item opens in the sheet's place, which closes it. (Closing it as well
  // would go Back, racing the item's page.)
  const finished = (done: Extract<UploadState, { kind: 'done' }>) => {
    onSending(null);
    void target.changed(done.item.id);
    say(done.existing ? target.words.existing : target.words.added);
    void navigate(target.opened(done.item.id), { replace: true });
  };

  if (state && state.kind !== 'done') {
    const up = uploader.current;
    const name = stage.kind === 'ready' ? stage.described.name : '';
    const sent = 'sent' in state ? state.sent : 0;
    const total = 'total' in state ? state.total : 1;
    return (
      <div className={s.sending}>
        {state.kind === 'failed' && <Banner tone="error">{uploadProblem(state.error)}</Banner>}
        {state.kind === 'preparing' || state.kind === 'finishing' ? (
          <div className={s.waiting}>
            <Spinner size="small" label={null} />
            <span>{state.kind === 'preparing' ? `Getting ${name} ready…` : target.words.finishing}</span>
          </div>
        ) : (
          <div className={s.progress}>
            <p className={s.progressText} aria-hidden="true">
              <span>
                {state.kind === 'paused' ? 'Paused' : state.kind === 'failed' ? 'Stopped' : `Sending ${name}`}
              </span>
              <span>
                {size(sent)} of {size(total)}
              </span>
            </p>
            <ProgressBar
              label={state.kind === 'paused' ? `${name}, paused` : `Sending ${name}`}
              value={total > 0 ? sent / total : 0}
              valueText={`${size(sent)} of ${size(total)}`}
            />
          </div>
        )}
        <div className={s.sendingActions}>
          {state.kind === 'sending' && (
            <Button variant="gray" onClick={() => up?.pause()}>
              Pause
            </Button>
          )}
          {(state.kind === 'paused' || state.kind === 'failed') && (
            <Button onClick={() => void up?.resume()}>{state.kind === 'failed' ? 'Try again' : 'Go on'}</Button>
          )}
          {state.kind !== 'cancelled' && state.kind !== 'finishing' && (
            <Button
              variant="destructivePlain"
              onClick={() => {
                void up?.cancel();
                onSending(null);
                setState(null);
                setStage({ kind: 'choose', problem: null });
              }}
            >
              Cancel sending
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={s.add}>
      {stage.kind === 'choose' && stage.problem && <Banner tone="error">{stage.problem}</Banner>}
      {stage.kind === 'ready' ? (
        <>
          <div className={s.described}>
            <ItemThumb url={null} kind={stage.described.kind} />
            <div>
              <p className={s.describedName}>{stage.described.name}</p>
              <p className={s.describedMeta}>
                {kindWord(stage.described.kind)} · {size(stage.file.size)} · {stage.file.name}
              </p>
            </div>
          </div>
          <p className={s.note}>{stage.next ? target.words.next(stage.next.name) : target.words.fresh}</p>
          <div className={s.sendingActions}>
            <Button onClick={send}>{stage.next ? 'Add the version' : 'Add it'}</Button>
            <Button variant="gray" onClick={() => setStage({ kind: 'choose', problem: null })}>
              Choose another file
            </Button>
          </div>
        </>
      ) : stage.kind === 'reading' ? (
        <div className={s.waiting}>
          <Spinner size="small" label={null} />
          <span>Reading {stage.file.name}…</span>
        </div>
      ) : (
        <FileDrop
          label={item ? `Choose the new version of ${item.name}` : 'Choose a dashboard or config'}
          hint="A dashboard (.tunetheme), or a config (.tuneconfig). It's read here first, to say what it is."
          accept={ACCEPT}
          file={null}
          onFile={(f) => void choose(f)}
        />
      )}
    </div>
  );
}
